import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { PoemEditor } from "./PoemEditor";

export default function PoemsTab({ book }) {
  const [poems, setPoems] = useState(null);
  const [forms, setForms] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState("free_verse");
  const load = useCallback(() => api.get(`/books/${book.id}/poems`).then((r) => { setPoems(r.data); setSel((s) => s || r.data[0]?.id || null); }), [book.id]);
  useEffect(() => { api.get("/poetry/forms").then((r) => setForms(r.data)); load(); }, [load]);
  if (!poems) return null;
  const add = async () => { try { const { data } = await api.post(`/books/${book.id}/poems`, { title: "Untitled", form }); await load(); setSel(data.id); } catch (e) { toast.error(errMsg(e)); } };
  const del = async (id) => { if (!window.confirm("Delete this poem?")) return; await api.delete(`/poems/${id}`); setSel(null); load(); };
  const move = async (id, dir) => { const ids = poems.map((p) => p.id); const i = ids.indexOf(id); const j = i + dir; if (j < 0 || j >= ids.length) return; [ids[i], ids[j]] = [ids[j], ids[i]]; await api.post(`/books/${book.id}/poems/reorder`, { ids }); load(); };
  const poem = poems.find((p) => p.id === sel);
  let lastSec = null;
  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-5" data-testid="poems-tab">
      <div className="space-y-2">
        <div className="flex gap-1">
          <select value={form} onChange={(e) => setForm(e.target.value)} className="flex-1 h-9 rounded-md bg-slate-900 border border-slate-700 px-2 text-xs" data-testid="poem-new-form">{forms.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}</select>
          <Button size="sm" onClick={add} className="h-9 bg-amber-600" data-testid="poem-add"><Plus size={13} className="mr-1" />Poem</Button>
        </div>
        <div className="space-y-1 max-h-[75vh] overflow-y-auto scrollbar-thin pr-1" data-testid="poem-list">
          {poems.map((p, i) => {
            const head = p.section && p.section !== lastSec ? p.section : null;
            lastSec = p.section || lastSec;
            return (
              <div key={p.id}>
                {head && <p className="text-[10px] uppercase tracking-wider text-amber-400/80 mt-3 mb-1">{head}</p>}
                <div onClick={() => setSel(p.id)} className={`group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer border ${p.id === sel ? "border-amber-400 bg-amber-500/10" : "border-transparent hover:bg-slate-900"}`} data-testid={`poem-item-${i}`}>
                  <span className="text-[10px] text-slate-500 w-5">{i + 1}</span><span className="text-sm truncate flex-1">{p.title}</span>
                  <span className="hidden group-hover:flex gap-1"><button onClick={(e) => { e.stopPropagation(); move(p.id, -1); }}><ArrowUp size={12} /></button><button onClick={(e) => { e.stopPropagation(); move(p.id, 1); }}><ArrowDown size={12} /></button>
                    <button onClick={(e) => { e.stopPropagation(); del(p.id); }} className="hover:text-red-400" data-testid={`poem-delete-${i}`}><Trash2 size={12} /></button></span>
                </div>
              </div>
            );
          })}
        </div>
        {!poems.length && <p className="text-sm text-slate-500" data-testid="poems-empty">Pick a form and add your first poem.</p>}
      </div>
      {poem ? <PoemEditor key={poem.id} poem={poem} forms={forms} reload={load} /> : <div className="panel p-10 text-center text-sm text-slate-400">Select or add a poem.</div>}
    </div>
  );
}
